import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api } from "../lib/api";

const PROMO_CODE = "ETERNEL10";
const SEEN_KEY = "ll_droplist_popup_seen";
const HIDE_PATHS = ["/admin", "/checkout"];

export default function DropListPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (HIDE_PATHS.some((p) => location.pathname.startsWith(p))) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), 3000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const close = () => {
    sessionStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please check the consent box so we can email you.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/subscribe", { email, phone: null, consent_email: true, consent_sms: false });
      sessionStorage.setItem(SEEN_KEY, "1");
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="droplist-popup"
          className="fixed inset-0 z-[79] flex items-end justify-center bg-black/75 sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={close}
        >
          <motion.div
            className="clip-card relative w-full max-w-md overflow-hidden border border-ll-line bg-ll-coal"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-label="Drop list email signup"
          >
            <div className="relative h-40 overflow-hidden">
              <img src="/images/products/led110-1.jpg" alt="" className="h-full w-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-ll-coal to-transparent" />
              <button data-testid="droplist-popup-close" onClick={close} aria-label="Close popup"
                      className="absolute right-3 top-3 bg-ll-black/70 p-2 transition-colors duration-200 hover:text-ll-red">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 pt-2">
              {!done ? (
                <>
                  <p className="font-tech text-[11px] tracking-[0.3em] text-ll-red">DROP LIST</p>
                  <h2 className="mt-2 font-display text-4xl leading-[0.9] tracking-wide">
                    GET 10% OFF<br />YOUR FIRST PIECE
                  </h2>
                  <p className="mt-3 text-sm text-ll-steel">
                    Drop your email — your 10% code lands in your inbox and unlocks right here, plus you get first word when the catalog opens. No spam.
                  </p>
                  <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
                    <div>
                      <label htmlFor="popup-email" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">EMAIL</label>
                      <input id="popup-email" data-testid="droplist-popup-email-input" type="email" value={email}
                             onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                             className="mt-1 w-full border border-ll-linestrong bg-ll-black px-4 py-3 text-sm placeholder:text-ll-steel/60 outline-none transition-colors duration-200 focus:border-ll-red" />
                    </div>
                    <label className="flex cursor-pointer items-start gap-3">
                      <input data-testid="droplist-popup-consent" type="checkbox" checked={consent}
                             onChange={(e) => setConsent(e.target.checked)}
                             className="mt-0.5 h-4 w-4 accent-ll-red" />
                      <span className="text-xs text-ll-steel">
                        I agree to receive email updates from ETERNEL about new pieces and restocks.
                      </span>
                    </label>
                    {error && <p data-testid="droplist-popup-error" className="border border-ll-red/50 bg-ll-red/10 px-3 py-2 text-xs text-ll-red">{error}</p>}
                    <motion.button data-testid="droplist-popup-submit" whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
                                   className="ll-lift w-full bg-ll-red py-4 font-tech text-sm tracking-[0.25em] hover:bg-ll-redhover disabled:opacity-60">
                      {loading ? "JOINING…" : "UNLOCK MY CODE"}
                    </motion.button>
                  </form>
                </>
              ) : (
                <div data-testid="droplist-popup-success" className="py-2 text-center">
                  <p className="font-tech text-[11px] tracking-[0.3em] text-ll-red">YOU'RE ON THE LIST</p>
                  <h2 className="mt-2 font-display text-4xl tracking-wide">YOUR 10% CODE</h2>
                  <p data-testid="droplist-popup-code" className="mx-auto mt-4 w-fit border border-ll-red bg-ll-red/10 px-8 py-3 font-tech text-2xl tracking-[0.3em]">
                    {PROMO_CODE}
                  </p>
                  <p className="mt-4 text-xs leading-relaxed text-ll-steel">
                    We also emailed it to you. Enter it at checkout when the catalog opens — 10% off your first order, one use.
                  </p>
                  <button data-testid="droplist-popup-done-close" onClick={close}
                          className="ll-lift mt-6 w-full bg-ll-red py-4 font-tech text-sm tracking-[0.25em] hover:bg-ll-redhover">
                    SHOP THE DROP
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
